import { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { AlertTriangle, Download, RefreshCw } from 'lucide-react';
import { Modal, SearchBox } from '../DashboardPrimitives';
import { InlineLoader } from '../../loading';
import StatTile from '../StatTile';
import { useTableSort, SortTh, type SortValue } from '../useTableSort';
import { buildCsv, downloadCsv } from '../../../utils/csv';
import type {
  Accent,
  BlockResult,
  ChartBlockConfig,
  DashboardBlock,
  KpiCardRowConfig,
  TableBlockConfig,
  TextBlockConfig,
  ValueFormat,
} from './types';

// Platform primary, identical to DynamicReportChart's DEFAULT_PRIMARY so a custom
// dashboard's charts match the AI report charts and the rest of the page chrome.
const PRIMARY = '#1B3A54';
const SERIES_PALETTE = [
  PRIMARY, '#16a34a', '#f97316', '#dc2626', '#9333ea',
  '#0891b2', '#ca8a04', '#db2777', '#65a30d',
];

// ── Value formatting ─────────────────────────────────────────────────────────

/**
 * Coerce a cell to a number. node-postgres returns NUMERIC/DECIMAL as a STRING, so
 * treating only `typeof v === 'number'` as numeric would leave most scores and
 * averages unformatted and unsortable.
 */
const toNumber = (v: unknown): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

export const formatValue = (v: unknown, format: ValueFormat = 'number'): string => {
  if (v === null || v === undefined) return '—';
  if (format === 'text') return String(v);
  const n = toNumber(v);
  // Not actually a number — render it as-is rather than showing "NaN" or a dash,
  // which would hide a real value the query returned.
  if (n === null) return typeof v === 'object' ? JSON.stringify(v) : String(v);

  if (format === 'percent') return `${Number.isInteger(n) ? n : n.toFixed(1)}%`;
  if (format === 'currency') return `₹${n.toLocaleString('en-IN')}`;
  return Number.isInteger(n) ? n.toLocaleString('en-IN') : n.toFixed(1);
};

/** Plain cell rendering for tables — mirrors DynamicReportChart.formatCell. */
const formatCell = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

// ── KPI card row ─────────────────────────────────────────────────────────────

// A card's accent comes out of a saved config, so it is read as text: one this page does
// not know is drawn in the plain tone.
const TONES: Record<string, Accent> = { default: 'default', good: 'good', bad: 'bad', warn: 'warn' };

/**
 * Headline strip. Each card is the dashboards' own headline tile (StatTile), so a
 * generated dashboard's cards cannot be told apart from a built-in dashboard's: the same
 * white frame, grey label, large number and short grey line.
 */
function KpiCardRow({ config, result }: { config: KpiCardRowConfig; result: BlockResult }) {
  const row = result.rows[0] ?? {};
  const cards = (config.cards ?? []).filter((c) => c && c.column);
  if (cards.length === 0) {
    return <div className="text-sm text-gray-500">No cards configured.</div>;
  }
  return (
    <section className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
      {cards.map((card) => {
        const missing = !result.columns.includes(card.column);
        return (
          <StatTile
            key={card.column}
            label={card.label}
            // A card naming a column the query no longer returns shows an explicit
            // dash rather than a blank — the block's warning explains why.
            value={missing ? '—' : formatValue(row[card.column], card.format)}
            helper={card.helper}
            hint={card.helper ?? card.label}
            valueTone={TONES[card.accent ?? 'default'] ?? 'default'}
          />
        );
      })}
    </section>
  );
}

// ── Table ────────────────────────────────────────────────────────────────────

const useColumnAccessors = (columns: string[]) =>
  useMemo(() => {
    const acc: Record<string, (row: Record<string, unknown>) => SortValue> = {};
    for (const c of columns) {
      acc[c] = (row) => {
        const v = row[c];
        if (v === null || v === undefined) return null;
        // Sort numeric strings numerically — pg hands back NUMERIC as text, so a
        // lexical sort would order 100 before 20.
        const n = toNumber(v);
        return n === null ? String(v) : n;
      };
    }
    return acc;
  }, [columns]);

function DataTable({
  columns,
  rows,
  label,
  compact,
}: {
  columns: string[];
  rows: Array<Record<string, unknown>>;
  label: (c: string) => string;
  compact?: boolean;
}) {
  const accessors = useColumnAccessors(columns);
  const sort = useTableSort(rows, accessors);
  const pad = compact ? 'px-3 py-2' : 'px-4 py-2.5';
  return (
    <div className="overflow-auto rounded-lg border border-gray-100">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
          <tr>
            {columns.map((c) => (
              <SortTh
                key={c}
                label={label(c)}
                sortKey={c}
                sort={sort}
                className={`${pad} font-medium whitespace-nowrap`}
                title={c}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {sort.sorted.length === 0 && (
            <tr>
              <td className={`${pad} text-gray-500`} colSpan={columns.length || 1}>
                No rows.
              </td>
            </tr>
          )}
          {sort.sorted.map((row, i) => (
            <tr key={i} className="border-t border-gray-100">
              {columns.map((c) => (
                <td key={c} className={`${pad} text-gray-800`}>
                  {formatCell(row[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TableBlock({
  block,
  config,
  result,
  label,
}: {
  block: DashboardBlock;
  config: TableBlockConfig;
  result: BlockResult;
  label: (c: string) => string;
}) {
  const [viewAll, setViewAll] = useState(false);
  const [search, setSearch] = useState('');

  // Only columns the result actually has — a stale config must not render a column
  // of empty cells.
  const columns = useMemo(() => {
    const known = new Set(result.columns);
    const chosen = (config.columns ?? []).filter((c) => known.has(c));
    return chosen.length > 0 ? chosen : result.columns;
  }, [config.columns, result.columns]);

  const topN = config.top_n ?? 10;
  const visible = result.rows.slice(0, topN);
  const hasMore = result.rows.length > visible.length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return result.rows;
    return result.rows.filter((row) =>
      columns.some((c) => formatCell(row[c]).toLowerCase().includes(q))
    );
  }, [result.rows, columns, search]);

  const handleDownload = () => {
    // Exports EVERY returned row, not the visible top-N — a download that silently
    // gave you 10 of 200 rows would be worse than no download.
    const csv = buildCsv(
      columns.map(label),
      result.rows.map((row) => columns.map((c) => formatCell(row[c])))
    );
    const slug = (block.title ?? 'dashboard-block').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    downloadCsv(`${slug}.csv`, csv);
  };

  return (
    <>
      <DataTable columns={columns} rows={visible} label={label} compact />
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] text-gray-400">
          {hasMore
            ? `Showing ${visible.length} of ${result.rowCount}`
            : `${result.rowCount} row${result.rowCount === 1 ? '' : 's'}`}
          {result.truncated && ` · capped at ${result.rowLimit}`}
        </p>
        <div className="flex items-center gap-2">
          {config.enable_view_all !== false && hasMore && (
            <button
              type="button"
              onClick={() => setViewAll(true)}
              className="rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 transition hover:bg-gray-50"
            >
              View all
            </button>
          )}
          {config.enable_download !== false && result.rowCount > 0 && (
            <button
              type="button"
              onClick={handleDownload}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 transition hover:bg-gray-50"
            >
              <Download className="h-3.5 w-3.5" />
              Download
            </button>
          )}
        </div>
      </div>

      {viewAll && (
        <Modal title={block.title ?? 'Details'} onClose={() => setViewAll(false)} maxWidth="max-w-6xl">
          <div className="mb-3">
            <SearchBox value={search} onChange={setSearch} placeholder="Search rows…" />
          </div>
          <p className="mb-2 text-[11px] text-gray-400">
            {filtered.length} of {result.rowCount} rows
          </p>
          <DataTable columns={columns} rows={filtered} label={label} />
        </Modal>
      )}
    </>
  );
}

// ── Chart ────────────────────────────────────────────────────────────────────

const HEIGHTS = { sm: 'h-56', md: 'h-72', lg: 'h-96' };

function ChartBlock({
  config,
  result,
  label,
}: {
  config: ChartBlockConfig;
  result: BlockResult;
  label: (c: string) => string;
}) {
  const height = HEIGHTS[config.height ?? 'md'];
  const known = new Set(result.columns);

  // Numeric coercion once, up front: recharts silently plots nothing for string
  // values, which is how a chart of pg NUMERIC columns renders as an empty grid.
  const seriesKeys = (config.seriesKeys ?? []).filter((k) => known.has(k));
  const valueKeys =
    seriesKeys.length > 0
      ? seriesKeys
      : [config.yKey ?? config.valueKey].filter((k): k is string => !!k && known.has(k));

  const data = useMemo(
    () =>
      result.rows.map((row) => {
        const out: Record<string, unknown> = { ...row };
        for (const k of valueKeys) out[k] = toNumber(row[k]) ?? 0;
        return out;
      }),
    [result.rows, valueKeys]
  );

  const xKey = config.xKey ?? config.nameKey;

  if (result.rowCount === 0) {
    return <div className={`${height} flex items-center justify-center text-sm text-gray-400`}>No data.</div>;
  }
  if (config.chartType !== 'pie' && (!xKey || valueKeys.length === 0)) {
    return (
      <div className="text-sm text-amber-600">
        This chart is missing its axis columns. Open it in the editor to re-point them.
      </div>
    );
  }

  if (config.chartType === 'pie') {
    const nameKey = config.nameKey ?? config.xKey;
    const valueKey = config.valueKey ?? config.yKey;
    if (!nameKey || !valueKey || !known.has(nameKey) || !known.has(valueKey)) {
      return (
        <div className="text-sm text-amber-600">
          This pie chart is missing its label or value column.
        </div>
      );
    }
    const pieData = result.rows.map((r) => ({
      name: formatCell(r[nameKey]),
      value: toNumber(r[valueKey]) ?? 0,
    }));
    return (
      <div className={`${height} w-full`}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={pieData} dataKey="value" nameKey="name" outerRadius="75%" label={(e) => e.name}>
              {pieData.map((_, i) => (
                <Cell key={i} fill={SERIES_PALETTE[i % SERIES_PALETTE.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  const axes = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
      <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
      <YAxis tick={{ fontSize: 11 }} />
      <Tooltip formatter={(v: unknown) => formatValue(v, config.yFormat)} />
      <Legend formatter={(v: string) => label(v)} />
    </>
  );

  return (
    <div className={`${height} w-full`}>
      <ResponsiveContainer width="100%" height="100%">
        {config.chartType === 'line' ? (
          <LineChart data={data}>
            {axes}
            {valueKeys.map((k, i) => (
              <Line
                key={k}
                type="monotone"
                dataKey={k}
                stroke={SERIES_PALETTE[i % SERIES_PALETTE.length]}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        ) : config.chartType === 'area' ? (
          <AreaChart data={data}>
            {axes}
            {valueKeys.map((k, i) => (
              <Area
                key={k}
                type="monotone"
                dataKey={k}
                stroke={SERIES_PALETTE[i % SERIES_PALETTE.length]}
                fill={SERIES_PALETTE[i % SERIES_PALETTE.length]}
                fillOpacity={0.18}
              />
            ))}
          </AreaChart>
        ) : (
          <BarChart data={data}>
            {axes}
            {valueKeys.map((k, i) => (
              <Bar
                key={k}
                dataKey={k}
                // stackId groups the series into one bar; without it a stacked_bar
                // renders as grouped bars, which reads as a different statistic.
                stackId={config.chartType === 'stacked_bar' ? 'a' : undefined}
                fill={SERIES_PALETTE[i % SERIES_PALETTE.length]}
                radius={[2, 2, 0, 0]}
              />
            ))}
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

// ── Block shell ──────────────────────────────────────────────────────────────

export function DashboardBlockCard({
  block,
  result,
  columnDisplay,
  onRetry,
  footer,
}: {
  block: DashboardBlock;
  result: BlockResult | undefined;
  columnDisplay: Record<string, string>;
  onRetry?: () => void;
  /** Author controls, rendered inside the block's own card so they scroll with it. */
  footer?: React.ReactNode;
}) {
  const label = (c: string): string => columnDisplay[c] ?? c;

  // Text blocks carry no query, so they render without a result.
  if (block.type === 'text') {
    return (
      // The same white card as every other block: its faint grey tint only stood out on a
      // white page, and the dashboards now sit on a grey one.
      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        {/* Rendered as ESCAPED text. The body is LLM-authored, so treating it as
            markup would make the model an XSS vector into a page other users load. */}
        <p className="whitespace-pre-wrap text-sm text-gray-600">
          {(block.config as TextBlockConfig).body}
        </p>
        {footer}
      </div>
    );
  }

  const header = (block.title || block.subtitle) && (
    <div className="mb-3">
      {block.title && <h3 className="text-lg font-semibold text-gray-900">{block.title}</h3>}
      {block.subtitle && <p className="mt-0.5 text-xs text-gray-500">{block.subtitle}</p>}
    </div>
  );

  // KPI strips render bare — a card-inside-a-card would double the border.
  const bare = block.type === 'kpi_card_row';
  const shell = bare ? '' : 'rounded-2xl border border-gray-200 bg-white p-4 shadow-sm';

  let body: React.ReactNode;
  if (!result) {
    body = <div className="py-6 text-center text-sm text-gray-400"><InlineLoader label="Loading…" className="flex justify-center" /></div>;
  } else if (result.error) {
    // Per-block failure isolation made visible: this block shows its own error and
    // its own retry, and every other block on the dashboard still renders.
    body = (
      <div className="rounded-lg border border-rose-100 bg-rose-50/60 px-3 py-3">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-rose-800">This block couldn’t load.</p>
            <p className="mt-0.5 break-words text-xs text-rose-700">{result.error}</p>
          </div>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-rose-200 bg-white px-2 py-1 text-xs font-medium text-rose-700 transition hover:bg-rose-50"
            >
              <RefreshCw className="h-3 w-3" />
              Retry
            </button>
          )}
        </div>
      </div>
    );
  } else if (block.type === 'kpi_card_row') {
    body = <KpiCardRow config={block.config as KpiCardRowConfig} result={result} />;
  } else if (block.type === 'chart') {
    body = <ChartBlock config={block.config as ChartBlockConfig} result={result} label={label} />;
  } else {
    body = (
      <TableBlock block={block} config={block.config as TableBlockConfig} result={result} label={label} />
    );
  }

  return (
    <div className={shell}>
      {!bare && header}
      {bare && block.title && (
        <h3 className="mb-2 text-lg font-semibold text-gray-900">{block.title}</h3>
      )}
      {body}
      {result?.warnings && result.warnings.length > 0 && !result.error && (
        <ul className="mt-2 space-y-0.5">
          {result.warnings.map((w, i) => (
            <li key={i} className="text-[11px] text-amber-600">
              {w}
            </li>
          ))}
        </ul>
      )}
      {footer}
    </div>
  );
}
